<script setup lang="ts">
import { fetchCommentReplyList } from '~/apis/comment/fetchCommentReplyList'
import { fetchResourceByCommentId } from '~/apis/comment/fetchResourceByCommentId'
import type { MLoadRef } from './MLoad.vue'

const props = defineProps<{
  commentId?: number
}>()

const { data: userInfo } = useAuth()

const page = ref(1)
const empty = ref(false)
const loaded = ref(false)
const commentList = ref<CommentSecondReplyDetail[]>([])
const loadRef = ref<MLoadRef>()

const { data } = await useAsyncData(() => {
  if (props.commentId) {
    return fetchCommentById(props.commentId)
  } else {
    throw createError({ statusCode: 404, message: '评论不见了' })
  }
})

let originCommentId = 0

if (data.value?.is_second_reply) {
  const { data: res } = await useAsyncData(() =>
    fetchResourceByCommentId(props.commentId!)
  )
  originCommentId = res.value?.floor_id || 0

  const { data: origin } = await useAsyncData(() => {
    if (props.commentId) {
      return fetchCommentById(originCommentId)
    } else {
      throw createError({ statusCode: 404, message: '评论不见了' })
    }
  })

  data.value = origin.value
}

onMounted(() => {
  load()
})

async function load() {
  page.value = 1
  empty.value = false
  if (props.commentId) {
    const res = await fetchCommentReplyList(props.commentId!, page.value)
    empty.value = res.length === 0
    commentList.value = res
    loaded.value = true
    loadRef.value?.check()
  } else {
    throw createError({ statusCode: 404, message: '评论不见了' })
  }
}

async function loadMore(ev?: { complete: () => void }) {
  console.log('loadmore')
  if (empty.value || !loaded.value) {
    ev?.complete()
    return
  }
  page.value++
  const more = await fetchCommentReplyList(props.commentId!, page.value)
  empty.value = more.length === 0
  commentList.value.push(...more)
  ev?.complete()
}
</script>

<template>
  <m-container title="评论详情" class="m-comment-detail">
    <div v-if="data" class="m-comment-detail__main">
      <div class="m-comment-detail__host">
        <MCommentDetailCard
          :key="data.id"
          :comment-area-id="data.comment_area_id"
          :comment-id="data.id"
          :floor="data.floor_num"
          :html="data.content"
          :image="data.content_ext?.images[0]"
          :user="data.user_info"
          :is-self="userInfo?.user.id === data.user_id"
          :is-delete="data.is_delete"
          :reply-count="+data.reply_count"
          :second-reply="data.second_reply"
          :created-at="data.created_at"
          :like-status="data.like_status"
          is-detail
          is-origin
        />
      </div>
      <div class="m-comment-detail__list">
        <MCommentDetailCard
          v-for="c in commentList"
          :key="c.id"
          :comment-id="c.id"
          :html="c.content"
          :user="c.user_info"
          :is-self="userInfo?.user.id === c.user_id"
          :is-delete="c.is_delete"
          :created-at="c.created_at"
          :like-status="c.like_status"
          is-detail
          @on-reply="load"
        />
        <MLoad ref="loadRef" :empty="empty" @on-scroll-end="loadMore" />
      </div>
    </div>
  </m-container>
</template>

<style lang="scss" scoped>
.m-comment-detail {
  width: 100%;
  &__main {
    max-height: 70vh;
    overflow-y: auto;
  }
  &__host {
    padding: 0 30px;
  }
  &__list {
    border-top: 8px solid var(--color-gray-100);
    padding: 0 30px;
    margin-top: 8px;
    > * {
      border-bottom: 1px solid var(--color-gray-100);
    }
  }
}

@media screen and (max-width: 600px) {
  .m-comment-detail {
    width: 100%;
    &__main {
      padding: 0 12px;
    }
    &__host {
      padding: 0;
    }
    &__list {
      padding: 0;
    }
  }
}
</style>
